import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Settings, User, Bell, Shield, Palette,
  Facebook, Instagram, Twitter, Youtube, Link2, Unlink,
  Save, Loader2, Zap, CheckCircle2, AlertCircle
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const DailyDropSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({
    niche: '',
    defaultPlatforms: ['instagram'],
    addWatermark: true,
    emailNotifications: true,
    streakReminders: true,
    weeklyReport: false
  });
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [userRes, settingsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/daily-drop/settings')
      ]);
      setUser(userRes.data);
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
      setConnectedAccounts(settingsRes.data?.connectedAccounts || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put('/daily-drop/settings', settings);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConnect = (platform) => {
    toast.info(`${platform} connection coming soon!`);
  };

  const handleDisconnect = (platform) => {
    setConnectedAccounts(prev => prev.filter(p => p.platform !== platform));
    toast.success(`${platform} disconnected`);
  };

  const platforms = [
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-pink-500 to-purple-500' },
    { id: 'twitter', name: 'X (Twitter)', icon: Twitter, color: 'bg-gray-800' },
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'bg-red-600' },
    { id: 'tiktok', name: 'TikTok', icon: () => <span className="text-lg">TT</span>, color: 'bg-gray-900' },
  ];

  const niches = [
    'Fitness & Health',
    'Food & Cooking',
    'Technology',
    'Fashion & Beauty',
    'Travel',
    'Business & Finance',
    'Entertainment',
    'Education',
    'Lifestyle',
    'Other'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-400" />
                Settings
              </h1>
              <p className="text-xs text-purple-300">Manage your DailyDrop preferences</p>
            </div>
          </div>
          <Button
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
            onClick={handleSave}
            disabled={isSaving}
            data-testid="save-settings-button"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Section */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="h-5 w-5 text-purple-400" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <img 
                src={user?.picture || `https://ui-avatars.com/api/?name=${user?.name || 'User'}&background=8B5CF6&color=fff`}
                alt={user?.name}
                className="w-16 h-16 rounded-full border-2 border-purple-500/50"
              />
              <div>
                <h3 className="text-white font-semibold">{user?.name}</h3>
                <p className="text-purple-300/70 text-sm">{user?.email}</p>
                <Badge className="mt-1 bg-purple-500/20 text-purple-300 border-purple-500/30">
                  {user?.plan || 'Free'} Plan
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-purple-200">Your Niche</Label>
              <select
                value={settings.niche}
                onChange={(e) => setSettings(prev => ({ ...prev, niche: e.target.value }))}
                className="w-full mt-1 bg-purple-500/10 border border-purple-500/30 text-white rounded-md p-2"
                data-testid="niche-select"
              >
                <option value="">Select your niche...</option>
                {niches.map(niche => (
                  <option key={niche} value={niche}>{niche}</option>
                ))}
              </select>
              <p className="text-purple-300/50 text-xs mt-1">This helps AI generate better content for you</p>
            </div>
          </CardContent>
        </Card>

        {/* Connected Accounts */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Link2 className="h-5 w-5 text-purple-400" />
              Connected Accounts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {platforms.map((platform) => {
              const isConnected = connectedAccounts.some(a => a.platform === platform.id);
              return (
                <div 
                  key={platform.id}
                  className="flex items-center justify-between p-4 bg-purple-500/10 rounded-lg"
                  data-testid={`platform-${platform.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`${platform.color} p-2 rounded-lg`}>
                      {typeof platform.icon === 'function' ? <platform.icon /> : <platform.icon className="h-5 w-5 text-white" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{platform.name}</p>
                      {isConnected ? (
                        <p className="text-green-400 text-xs flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Connected
                        </p>
                      ) : (
                        <p className="text-purple-300/50 text-xs flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Not connected
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant={isConnected ? 'destructive' : 'outline'}
                    size="sm"
                    className={!isConnected ? 'border-purple-500/50 text-purple-200 hover:bg-purple-500/20' : ''}
                    onClick={() => isConnected ? handleDisconnect(platform.id) : handleConnect(platform.name)}
                    data-testid={`${isConnected ? 'disconnect' : 'connect'}-${platform.id}`}
                  >
                    {isConnected ? (
                      <>
                        <Unlink className="h-4 w-4 mr-1" /> Disconnect
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-1" /> Connect
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
            <p className="text-center text-purple-300/50 text-sm pt-2">
              Platform connections are coming soon. Stay tuned!
            </p>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Bell className="h-5 w-5 text-purple-400" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Email Notifications</Label>
                <p className="text-purple-300/50 text-xs">Get updates about your account</p>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={(val) => setSettings(prev => ({ ...prev, emailNotifications: val }))}
                data-testid="email-notifications-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Streak Reminders</Label>
                <p className="text-purple-300/50 text-xs">Daily reminder to keep your streak</p>
              </div>
              <Switch
                checked={settings.streakReminders}
                onCheckedChange={(val) => setSettings(prev => ({ ...prev, streakReminders: val }))}
                data-testid="streak-reminders-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Weekly Report</Label>
                <p className="text-purple-300/50 text-xs">Summary of your weekly performance</p>
              </div>
              <Switch
                checked={settings.weeklyReport}
                onCheckedChange={(val) => setSettings(prev => ({ ...prev, weeklyReport: val }))}
                data-testid="weekly-report-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Content Defaults */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Palette className="h-5 w-5 text-purple-400" />
              Content Defaults
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Add Watermark</Label>
                <p className="text-purple-300/50 text-xs">Add TerryLBPrompts.com to your content</p>
              </div>
              <Switch
                checked={settings.addWatermark}
                onCheckedChange={(val) => setSettings(prev => ({ ...prev, addWatermark: val }))}
                data-testid="watermark-switch"
              />
            </div>
            <div>
              <Label className="text-purple-200">Default Platforms</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {platforms.map((p) => (
                  <Badge
                    key={p.id}
                    variant={settings.defaultPlatforms?.includes(p.id) ? 'default' : 'outline'}
                    className={`cursor-pointer ${settings.defaultPlatforms?.includes(p.id) 
                      ? 'bg-purple-600' 
                      : 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20'}`}
                    onClick={() => {
                      setSettings(prev => ({
                        ...prev,
                        defaultPlatforms: prev.defaultPlatforms?.includes(p.id)
                          ? prev.defaultPlatforms.filter(id => id !== p.id)
                          : [...(prev.defaultPlatforms || []), p.id]
                      }));
                    }}
                    data-testid={`default-platform-${p.id}`}
                  >
                    {p.name}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-red-500/10 border-red-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-red-300 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Delete All Posts</p>
                <p className="text-red-300/70 text-xs">Permanently delete all your DailyDrop posts</p>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => toast.error('This action is disabled for safety')}
                data-testid="delete-posts-button"
              >
                Delete All
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade CTA */}
        {user?.plan === 'free' && (
          <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-amber-500/30 p-3 rounded-xl">
                    <Zap className="h-6 w-6 text-amber-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Unlock Premium Features</h3>
                    <p className="text-amber-200/70 text-sm">
                      Unlimited posts, advanced analytics, remove watermarks & more
                    </p>
                  </div>
                </div>
                <Button
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
                  onClick={() => navigate('/subscription')}
                  data-testid="upgrade-settings-button"
                >
                  Upgrade Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default DailyDropSettings;
